// src/pages/MobileUploadPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Or next/navigation if using Next.js App Router
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
// --- Firestore Imports ---
import { doc, updateDoc } from 'firebase/firestore';
import { storage, db } from '@/firebase/firebase'; // Import 'db' along with 'storage'
// --- End Firestore Imports ---
import { Button } from '@/components/button'; // Assuming path is correct
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Camera, Loader2, UploadCloud } from 'lucide-react'; // Import icons

// Optional: Function to validate token (requires another Cloud Function)
// You'd typically call this in useEffect to verify the token server-side on page load
// import { httpsCallable } from 'firebase/functions';
// import { functions } from '@/firebase/firebase';
// const validateUploadTokenFunction = httpsCallable(functions, 'validateUploadToken');

export default function MobileUploadPage() {
    const { tokenId } = useParams(); // Get token ID from the URL parameter
    const navigate = useNavigate(); // Optional: for redirecting after completion/error
    const [file, setFile] = useState(null); // Holds the selected File object
    const [uploading, setUploading] = useState(false); // Tracks if upload is in progress
    const [uploadProgress, setUploadProgress] = useState(0); // Upload progress percentage
    const [error, setError] = useState(''); // Stores error messages for display
    const [isTokenValid, setIsTokenValid] = useState(true); // Assume valid initially, backend trigger confirms fully
    const [isLoadingToken, setIsLoadingToken] = useState(false); // If you add async token validation
    const [uploadComplete, setUploadComplete] = useState(false); // Flag for success state
    const fileInputRef = useRef(null); // Ref to access the hidden file input

    // Optional: Add useEffect hook here to call a backend function
    // to validate the tokenId on page load for better UX.
    // useEffect(() => {
    //     async function validateToken() {
    //         if (!tokenId) { setError("Invalid link: No token."); setIsTokenValid(false); return; }
    //         setIsLoadingToken(true);
    //         try {
    //             // --- Example Server-Side Validation ---
    //             // console.log(`[MobileUpload] Validating token server-side: ${tokenId}`);
    //             // const result = await validateUploadTokenFunction({ tokenId }); // Call backend Cloud Function
    //             // console.log("[MobileUpload] Token validation result:", result.data);
    //             // if (!result.data.isValid) {
    //             //     setError(`Link invalid or expired. Reason: ${result.data.reason || 'Unknown'}`);
    //             //     setIsTokenValid(false);
    //             // } else {
    //             //     setIsTokenValid(true); // Token confirmed valid by backend
    //             // }
    //             // --- End Example Server-Side Validation ---

    //             // For now, if not doing server-side validation, just assume okay
    //             // The actual upload will fail if the token doc doesn't exist or rules prevent write.
    //              setIsTokenValid(true); // Assuming valid for now
    //         } catch (err) {
    //             setError("Error verifying link. It might be invalid or expired.");
    //             setIsTokenValid(false);
    //             console.error("[MobileUpload] Token validation function error:", err);
    //         } finally {
    //             setIsLoadingToken(false);
    //         }
    //     }
    //     validateToken();
    // }, [tokenId]);

    // Handles file selection from the input
    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            console.log("[MobileUpload] File selected:", selectedFile.name, selectedFile.type);
            // Basic validation example
            const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
            if (!allowedTypes.includes(selectedFile.type)) {
                 setError("Invalid file type. Please upload an image (JPG, PNG, WEBP, HEIC) or PDF.");
                 setFile(null); // Clear invalid file
                 event.target.value = ''; // Reset input visually
                 return;
            }
            // Optional: Add file size validation
             const maxSizeMB = 15; // Increased limit for potentially large PDFs/HEIC
             if (selectedFile.size > maxSizeMB * 1024 * 1024) {
                setError(`File too large. Maximum size is ${maxSizeMB}MB.`);
                setFile(null);
                event.target.value = '';
                return;
             }

            setFile(selectedFile);
            setError(''); // Clear previous errors on new selection
            setUploadComplete(false); // Reset complete state if user selects a new file
        }
    };

    // Handles the actual upload process
    const handleUpload = async () => {
        // Prevent upload if conditions not met
        if (!file || !tokenId || !isTokenValid) {
            setError("Cannot upload: No file selected or link is invalid.");
            return;
        }
        if (uploading) return; // Prevent multiple simultaneous uploads

        setUploading(true);
        setUploadProgress(0);
        setError('');
        setUploadComplete(false); // Ensure complete state is false before starting
        toast.loading("Starting upload...", { id: 'upload-toast' }); // Use ID for toast updates

        // Storage path (adjust if needed, e.g., if not using Storage Triggers)
        const storagePath = `pendingVerificationUploads/${tokenId}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, storagePath);

        // Metadata (useful for Storage Triggers or backend functions)
        const metadata = {
            contentType: file.type,
            customMetadata: {
                'uploadTokenId': tokenId, // Token ID for backend association
                'originalName': file.name  // Original file name
            }
        };

        console.log(`[MobileUpload] Uploading to: ${storagePath} with metadata including uploadTokenId: ${tokenId}`);
        const uploadTask = uploadBytesResumable(storageRef, file, metadata);

        // Listen for state changes, errors, and completion of the upload.
        uploadTask.on('state_changed',
            (snapshot) => {
                // Get task progress
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log('[MobileUpload] Upload is ' + progress + '% done');
                setUploadProgress(progress);
                // Update loading toast with progress
                toast.loading(`Uploading: ${Math.round(progress)}%`, { id: 'upload-toast' });
            },
            (uploadError) => {
                // Handle unsuccessful Storage uploads
                console.error("[MobileUpload] Storage Upload failed:", uploadError);
                toast.dismiss('upload-toast'); // Dismiss loading toast by ID

                // Provide specific user feedback based on error codes
                let userMessage = 'Upload failed. Please try again.';
                switch (uploadError.code) {
                    case 'storage/unauthorized':
                        userMessage = "Permission denied. You might need to refresh the QR code link or check network connection.";
                        // Check Storage rules for 'pendingVerificationUploads/' path if this persists.
                        // Rules should likely allow writes based on the token's existence/status or user auth.
                        break;
                    case 'storage/canceled':
                        userMessage = "Upload cancelled.";
                        break;
                     case 'storage/object-not-found': // Might indicate token ID mismatch or folder issue
                        userMessage = "Upload error: Target location not found. Please refresh QR code.";
                        break;
                     case 'storage/quota-exceeded':
                        userMessage = "Storage quota exceeded. Please contact support.";
                        break;
                    case 'storage/unknown':
                    default:
                        userMessage = `Upload failed due to an unknown error. Please try again later.`;
                        break;
                }
                setError(userMessage);
                setUploading(false);
                setUploadProgress(0);
                toast.error(userMessage); // Show error toast
            },
            // ----- VVVVV STORAGE UPLOAD COMPLETION CALLBACK VVVVV -----
            async () => {
                // Handle successful Storage uploads on complete
                console.log('[MobileUpload] Storage upload complete! Attempting to finalize...');
                toast.dismiss('upload-toast'); // Dismiss the initial loading toast
                setUploading(false); // Mark storage upload part as done locally
                setUploadProgress(100); // Show 100% momentarily

                // --- !!! ATTEMPT FIRESTORE UPDATE !!! ---
                try {
                    // Show an intermediate processing state
                    toast.loading("Processing upload...", { id: 'finalize-toast' });
                    console.log(`[MobileUpload] Attempting to update Firestore document: uploadTokens/${tokenId}`);
                    const tokenDocRef = doc(db, 'uploadTokens', tokenId);

                    // ** THIS IS THE CRITICAL TRIGGER **
                    // Update the status to 'completed' (or 'success')
                    // This tells the listener on the desktop page that the upload is done.
                    await updateDoc(tokenDocRef, {
                        status: 'completed', // MUST MATCH what CertifiedVerification listener expects!
                        // Optionally store the final storage path or download URL if needed later by backend
                        // finalStoragePath: storagePath,
                        // uploadedAt: serverTimestamp(), // Requires importing serverTimestamp
                    });

                    console.log(`[MobileUpload] Successfully updated Firestore status for token ${tokenId} to 'completed'`);
                    toast.dismiss('finalize-toast'); // Dismiss processing toast

                    // Now show the final success message to the user on mobile
                    setUploadComplete(true); // Indicate entire process finished successfully
                    toast.success("File Uploaded Successfully! You can close this window.", { duration: 6000 });

                    // Clear file state after successful upload and Firestore update
                    setFile(null);
                    if(fileInputRef.current) {
                        fileInputRef.current.value = ''; // Reset file input visually
                    }

                    // Optional: Automatically navigate away or close window after a delay
                    // setTimeout(() => {
                    //    // window.close(); // May not work reliably on all mobile browsers
                    //    // navigate('/upload-success'); // Or redirect to a dedicated success page
                    // }, 3000);

                } catch (firestoreError) {
                    console.error(`[MobileUpload] Failed to update Firestore status for token ${tokenId}:`, firestoreError);
                    toast.dismiss('finalize-toast'); // Dismiss processing toast
                    // Inform the user that the final step failed
                    setError("Upload successful, but failed to finalize. Please notify support or try again.");
                    toast.error("Upload succeeded but couldn't finalize processing. Please contact support.");
                    // Keep upload state as not fully complete maybe?
                    // setUploadComplete(false); // Or handle this state differently
                    setUploading(false); // Ensure uploading is definitely false
                    setUploadProgress(0); // Reset progress as final step failed
                }
                // --- !!! END OF FIRESTORE UPDATE LOGIC !!! ---
            }
            // ----- ^^^^^ END OF STORAGE UPLOAD COMPLETION CALLBACK ^^^^^ -----
        );
    };

    // --- Render Logic ---

    // Show loading state if validating token asynchronously
    if (isLoadingToken) {
        return (
            <div className="flex justify-center items-center min-h-screen p-4 bg-gray-100 dark:bg-gray-800">
                <div className="text-center text-zinc-600 dark:text-zinc-400">
                    <Loader2 className="animate-spin inline mr-2 h-6 w-6"/> Verifying upload link...
                </div>
            </div>
        );
    }

    // Show error state if token is invalid or initial validation failed
    if (!isTokenValid) {
         return (
             <div className="flex justify-center items-center min-h-screen p-4 bg-gray-100 dark:bg-gray-800">
                <div className="text-center text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 p-6 rounded-lg shadow border border-red-300 dark:border-red-700">
                    <XCircle className="inline mr-2 h-6 w-6"/> {error || "This upload link is invalid or expired."}
                    <p className="text-sm mt-2 text-zinc-600 dark:text-zinc-400">Please request a new QR code link from the verification page.</p>
                </div>
             </div>
         );
    }

    // Main upload UI
    return (
        <div className="flex justify-center items-start min-h-screen p-4 bg-gray-100 dark:bg-gray-800">
            <div className="bg-white dark:bg-zinc-900 w-full max-w-md rounded-lg shadow-xl p-6 mt-8 border border-gray-200 dark:border-zinc-700">
                <h1 className="text-xl font-bold mb-2 text-center text-zinc-900 dark:text-zinc-100">Upload Verification Document</h1>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6 text-center">Use your phone's camera or select a file (Image or PDF).</p>

                {/* Hidden file input */}
                 <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" // Specific accepted types
                    // Using 'capture' can sometimes be buggy or inconsistent across devices/browsers
                    // It's often better to let the user choose between camera/file picker
                    // capture="environment" // Prioritize rear camera for capture
                    onChange={handleFileChange}
                    className="hidden"
                    id="mobile-file-upload-input"
                    disabled={uploading || uploadComplete} // Disable after completion or during upload
                 />

                {/* Button to trigger file input */}
                {!uploadComplete && ( // Only show if not completed
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        variant="outline" // Style as needed
                        className="w-full mb-4 text-zinc-700 dark:text-zinc-300 border-zinc-300 dark:border-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                        disabled={uploading} // Disable while uploading
                        aria-label={file ? 'Change selected file' : 'Take photo or choose file'}
                    >
                        <Camera className="inline-block w-5 h-5 mr-2"/>
                        {file ? 'Change Selection' : 'Take Photo / Choose File'}
                    </Button>
                )}


                {/* Display selected file info and progress */}
                {file && (
                    <div className="text-center text-sm mb-4 p-3 border rounded-md dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/50">
                        <p className="font-medium text-zinc-800 dark:text-zinc-200 truncate px-2">{file.name}</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">({(file.size / 1024 / 1024).toFixed(2)} MB)</p>
                         {/* Display progress bar */}
                         {uploading && (
                             <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 mt-2.5 mb-1 overflow-hidden">
                                 <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-150 ease-out" style={{ width: `${uploadProgress}%` }}></div>
                             </div>
                         )}
                    </div>
                )}

                {/* Upload Button - Only show if a file is selected and not completed */}
                {file && !uploadComplete && (
                    <Button
                        onClick={handleUpload}
                        disabled={uploading} // Disable while uploading
                        className="w-full" // Use default button style or customize
                    >
                        {uploading ? (
                             <> <Loader2 className="animate-spin inline mr-2 h-4 w-4"/> Uploading... ({Math.round(uploadProgress)}%)</>
                        ) : (
                             <> <UploadCloud className="inline-block w-5 h-5 mr-2"/> Upload Document </>
                        )}
                    </Button>
                )}


                {/* Error Display */}
                {error && !uploading && ( // Only show error if not currently uploading
                     <p className="text-red-600 dark:text-red-400 text-sm mt-4 text-center px-2">{error}</p>
                 )}

                 {/* Success Indication */}
                 {uploadComplete && (
                     <div className="text-green-700 dark:text-green-400 text-sm mt-4 text-center flex items-center justify-center p-4 bg-green-100 dark:bg-green-900/40 rounded-md border border-green-300 dark:border-green-700">
                         <CheckCircle className="inline mr-2 h-5 w-5"/> Upload Successful! You may close this window.
                     </div>
                 )}
            </div>
        </div>
    );
}